import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const salesServicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales/services',
  component: SalesServicesPage,
});

type CreateValues = {
  name: string;
  sortOrder: number;
  active: boolean;
};

function SalesServicesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['sales-services'],
    queryFn: () => api.listServiceOfferings(),
  });

  const form = useForm<CreateValues>({
    defaultValues: { name: '', sortOrder: 0, active: true },
  });

  const upsertMutation = useMutation({
    mutationFn: (v: CreateValues) =>
      api.upsertServiceOffering({
        name: v.name.trim(),
        sortOrder: Number(v.sortOrder),
        active: v.active,
      }),
    onSuccess: async () => {
      form.reset({ name: '', sortOrder: 0, active: true });
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['sales-services'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateServiceOffering(id),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['sales-services'] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const items = listQuery.data ?? [];

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Services</h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Service catalog used for prospect interests. Admin only.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Add or update</CardTitle>
          <CardDescription>Names are unique. Use sort order for grouping.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={form.handleSubmit((v) => upsertMutation.mutate(v))}
            className="grid gap-3 md:grid-cols-4 md:items-end"
          >
            <div className="md:col-span-2">
              <Label>Name</Label>
              <Input {...form.register('name', { required: true })} placeholder="e.g. Growth advisory" />
            </div>
            <div>
              <Label>Sort order</Label>
              <Input type="number" min="0" {...form.register('sortOrder', { valueAsNumber: true })} />
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" {...form.register('active')} />
                Active
              </label>
              <Button type="submit" disabled={upsertMutation.isPending}>
                {upsertMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Catalog</CardTitle>
          <CardDescription>{items.length} services.</CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No services yet.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {items.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      sort {s.sortOrder}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={s.active ? 'default' : 'muted'}>
                      {s.active ? 'active' : 'inactive'}
                    </Badge>
                    {s.active ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deactivateMutation.mutate(s.id)}
                        disabled={deactivateMutation.isPending}
                      >
                        Deactivate
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
